// src/components/LiveSpawnMap.js
import React, { useEffect, useRef, useState } from 'react';
import { firestore, collection, query, where, onSnapshot } from '../firebase-config';
import './LiveSpawnMap.css';

const LiveSpawnMap = () => {
  const canvasRef = useRef(null);
  const [blocks, setBlocks] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewportOffset, setViewportOffset] = useState({ x: 0, z: 0 });

  // Define spawn area bounds (adjust these based on your server)
  const SPAWN_BOUNDS = {
    minX: -100,
    maxX: 100,
    minZ: -100,
    maxZ: 100
  };

  const PIXEL_SIZE = 4; // Each block = 4x4 pixels on canvas
  const MAP_WIDTH = SPAWN_BOUNDS.maxX - SPAWN_BOUNDS.minX;
  const MAP_HEIGHT = SPAWN_BOUNDS.maxZ - SPAWN_BOUNDS.minZ;

  useEffect(() => {
    // Query blocks within spawn area
    const blocksRef = collection(firestore, 'blocks');
    const q = query(
      blocksRef,
      where('x', '>=', SPAWN_BOUNDS.minX),
      where('x', '<=', SPAWN_BOUNDS.maxX)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newBlocks = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Filter Z coordinate client-side (Firestore limitation)
        if (data.z >= SPAWN_BOUNDS.minZ && data.z <= SPAWN_BOUNDS.maxZ) {
          const key = `${data.x}_${data.z}`;
          newBlocks[key] = {
            x: data.x,
            z: data.z,
            hex: data.hex || '#FFFFFF',
            colour: data.colour || 'WHITE',
            lastPainter: data.lastPainter
          };
        }
      });
      setBlocks(newBlocks);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = MAP_WIDTH * PIXEL_SIZE;
    const height = MAP_HEIGHT * PIXEL_SIZE;

    canvas.width = width;
    canvas.height = height;

    // Clear canvas with white background (unpainted blocks)
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);

    // Draw grid lines (subtle)
    // ctx.strokeStyle = '#f0f0f0';
    // ctx.lineWidth = 0.5;
    // for (let x = 0; x <= MAP_WIDTH; x++) {
    //   ctx.beginPath();
    //   ctx.moveTo(x * PIXEL_SIZE, 0);
    //   ctx.lineTo(x * PIXEL_SIZE, height);
    //   ctx.stroke();
    // }
    // for (let z = 0; z <= MAP_HEIGHT; z++) {
    //   ctx.beginPath();
    //   ctx.moveTo(0, z * PIXEL_SIZE);
    //   ctx.lineTo(width, z * PIXEL_SIZE);
    //   ctx.stroke();
    // }

    // Draw painted blocks
    Object.values(blocks).forEach(block => {
      const canvasX = (block.x - SPAWN_BOUNDS.minX) * PIXEL_SIZE;
      const canvasZ = (block.z - SPAWN_BOUNDS.minZ) * PIXEL_SIZE;

      ctx.fillStyle = block.hex;
      ctx.fillRect(canvasX, canvasZ, PIXEL_SIZE, PIXEL_SIZE);
    });

  }, [blocks]);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;

    const blockX = Math.floor(clickX / PIXEL_SIZE) + SPAWN_BOUNDS.minX;
    const blockZ = Math.floor(clickY / PIXEL_SIZE) + SPAWN_BOUNDS.minZ;
    const key = `${blockX}_${blockZ}`;

    const block = blocks[key];
    if (block) {
      alert(`Block at (${blockX}, ${blockZ})\nColour: ${block.colour}\nLast painted by: ${block.lastPainter}`);
    } else {
      alert(`Block at (${blockX}, ${blockZ})\nNot painted yet`);
    }
  };

  return (
    <div className="live-spawn-map">
      <div className="map-header">
        <h3>Live Spawn Area</h3>
        <span className="map-info">
          {Object.keys(blocks).length} blocks in spawn
        </span>
      </div>
      {loading ? (
        <div className="map-loading">Loading map...</div>
      ) : (
        <div className="map-container">
          <canvas 
            ref={canvasRef}
            onClick={handleCanvasClick}
            style={{ cursor: 'pointer' }}
          />
        </div>
      )}
      <div className="map-legend">
        <small>Click a block to see details</small>
      </div>
    </div>
  );
};

export default LiveSpawnMap;